<script setup lang="ts">
export interface SocialLink {
	service: string;
	url: string;
}

export interface NavigationItem {
	id: string;
	title: string;
	url?: string | null;
	page?: {
		permalink?: string | null;
	};
}

export interface FooterProps {
	navigation: {
		items: NavigationItem[];
	};
	globals: {
		logo?: string | null;
		logo_dark_mode?: string | null;
		description?: string | null;
		social_links?: SocialLink[];
		contacts_email?: string | null;
		contacts_phone?: string | null;
		contacts_address?: string | null;
	};
}

const props = defineProps<FooterProps>();
const runtimeConfig = useRuntimeConfig();

// Using template ref to expose the footer to the layout for visual editing
const footerRef = useTemplateRef('footerRef');
defineExpose({ footerRef });

const darkLogoUrl = computed(() =>
	props.globals.logo_dark_mode ? `${runtimeConfig.public.directusUrl}/assets/${props.globals.logo_dark_mode}` : '',
);
</script>

<template>
	<footer v-if="globals" ref="footerRef" class="bg-black py-16">
		<Container class="text-white">
			<div class="flex flex-col md:flex-row items-start md:items-center gap-8 pt-8">
				<div class="flex-1">
					<h2 class="text-white text-xl font-semibold mb-4">Contatti</h2>
					
					<div class="space-y-2 text-white">
						<p v-if="props.globals.contacts_email">
							<span class="font-bold">Email: </span>
							<a :href="`mailto:${props.globals.contacts_email}`" class="hover:underline">
								{{ props.globals.contacts_email }}
							</a>
						</p>
						<p v-if="props.globals.contacts_phone">
							<span class="font-bold">Telefono: </span>
							<a :href="`tel:${props.globals.contacts_phone}`" class="hover:underline">
								{{ props.globals.contacts_phone }}
							</a>
						</p>
						<p v-if="props.globals.contacts_address">
							<span class="font-bold">Indirizzo: </span>
							<a
								:href="`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(props.globals.contacts_address)}`"
								target="_blank"
								rel="noopener noreferrer"
								class="hover:underline"
							>
								{{ props.globals.contacts_address }}
							</a>
						</p>
					</div>

					<!-- Social Links -->
					<div v-if="props.globals.social_links?.length" class="mt-4 flex space-x-4">
						<a
							v-for="social in props.globals.social_links"
							:key="social.service"
							:href="social.url"
							target="_blank"
							rel="noopener noreferrer"
							class="social-icon-link size-8 rounded bg-transparent inline-flex items-center justify-center transition-all duration-300"
						>
							<img
								:src="`/icons/social/${social.service}.svg`"
								:alt="`${social.service} icon`"
								class="social-icon size-6 invert transition-all duration-300"
							/>
						</a>
					</div>
				</div>

				<!-- <div class="flex flex-col items-start md:items-end flex-1">
					<nav v-if="props.navigation.items?.length" class="w-full md:w-auto text-left">
						<ul class="space-y-4">
							<li v-for="item in props.navigation.items" :key="item.id">
								<NuxtLink
									v-if="item.page?.permalink"
									:to="item.page.permalink"
									class="text-white font-medium hover:underline"
								>
									{{ item.title }}
								</NuxtLink>
								<a v-else :href="item.url || '#'" class="text-white font-medium hover:underline">
									{{ item.title }}
								</a>
							</li>
						</ul>
						<ThemeToggle class="dark:text-white mt-4" />
					</nav>
				</div> -->

				<div class="flex-1 flex flex-col items-center md:items-end justify-center self-stretch">
					<NuxtLink v-if="darkLogoUrl" to="/" class="inline-block transition-opacity hover:opacity-70 h-full flex items-center justify-center md:justify-end w-full">
						<img
							:src="darkLogoUrl"
							alt="Logo"
							class="max-w-[240px] h-full object-contain"
						/>
					</NuxtLink>
				</div>
			</div>
		</Container>
	</footer>
</template>

<style scoped>
/* Usa il colore #A21B1F (rosso scuro) per le icone all'hover */
.social-icon-link:hover .social-icon {
	/* Rimuove l'invert e applica il colore #A21B1F */
	/* Filtro calcolato per convertire il bianco in #A21B1F */
	filter: brightness(0) saturate(40%) invert(13%) sepia(94%) saturate(3000%) hue-rotate(350deg) brightness(95%) contrast(89%);
}
</style>
